import { useState, type FC } from "react";
import { Link as LinkIcon } from "lucide-react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import useDocumentLink from "@/hooks/useDocumentLink";
import {
  showErrorToast,
  showSuccessToast,
  showWarningToast,
} from "@/components/custom-toasts/toasts";
import { buildDocumentLinkUrl } from "@/utils/domain";
import { DataSource, normalizeDataSource } from "@/config/datasources";
import { ReferenceTypePicker } from "@/components/document-link/ReferenceTypePicker";
import {
  buildDocumentReferenceRequest,
  type DocumentReferenceTarget,
} from "@/lib/documentReferenceRequest";
import { logger } from "@/lib/logger";

/**
 * The new reference is a template until the copied link is pasted somewhere, so
 * it deliberately does not show up in the list below right away.
 */
const CREATE_REFERENCE_TOOLTIP =
  "Opretter en reference til hele dokumentet og kopierer linket til udklipsholderen. Referencen vises i listen, når linket er indsat i et andet dokument.";

type CreateDocumentReferenceButtonProps = {
  target: DocumentReferenceTarget;
};

export const CreateDocumentReferenceButton: FC<
  CreateDocumentReferenceButtonProps
> = ({ target }) => {
  const { postTemplate } = useDocumentLink();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [referenceType, setReferenceType] = useState<"static" | "nonstatic">(
    "nonstatic"
  );

  const isPending = postTemplate.isPending;
  const destinationDocumentId =
    target.consolidation?.lawExternalId || target.documentId;
  const isDisabled = !destinationDocumentId || !target.source;

  // Static/non-static is only meaningful for Retsinformation (LBK supersession).
  const isRetsinformationLegislation =
    normalizeDataSource(target.source || "") === DataSource.Retsinformation;

  const createReference = async (isStatic: boolean) => {
    const request = buildDocumentReferenceRequest(target, isStatic);

    try {
      const referenceId = await postTemplate.mutateAsync(request);
      if (!referenceId) {
        logger.error("Document link template created without an id", {
          operation: "createDocumentReference",
          error: new Error("Empty document link template response"),
          documentId: request.destinationDocumentId,
          source: request.destinationSource,
          workspaceId: request.destinationWorkspaceId,
        });
        showErrorToast("Referencen kunne ikke oprettes");
        return;
      }

      const documentLinkUrl = buildDocumentLinkUrl(referenceId);
      try {
        await navigator.clipboard.writeText(documentLinkUrl);
        showSuccessToast("Reference oprettet og kopieret til udklipsholderen");
      } catch (clipboardError) {
        logger.warn("Failed to copy document link to the clipboard", {
          operation: "createDocumentReference",
          documentId: request.destinationDocumentId,
          error: clipboardError,
        });
        showWarningToast(
          "Referencen blev oprettet, men linket kunne ikke kopieres til udklipsholderen"
        );
      }
    } catch (error) {
      logger.error("Failed to create document reference", {
        operation: "createDocumentReference",
        error,
        documentId: request.destinationDocumentId,
        source: request.destinationSource,
        workspaceId: request.destinationWorkspaceId,
        changeLaw: target.consolidation?.changeLaw,
      });
      showErrorToast("Referencen kunne ikke oprettes");
    } finally {
      setIsPopoverOpen(false);
    }
  };

  const handleClick = async () => {
    if (isRetsinformationLegislation) {
      setReferenceType("nonstatic");
      setIsPopoverOpen(true);
      return;
    }
    await createReference(true);
  };

  const triggerButton = (
    <Button
      id="create-document-reference-button"
      type="button"
      variant="outline"
      size="sm"
      className="w-full justify-center"
      disabled={isDisabled || isPending}
      onClick={handleClick}
    >
      {isPending ? (
        <Spinner size="small" className="mr-2" />
      ) : (
        <LinkIcon className="mr-2 size-4" aria-hidden />
      )}
      Opret reference
    </Button>
  );

  if (!isRetsinformationLegislation) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{triggerButton}</TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs">
          {CREATE_REFERENCE_TOOLTIP}
        </TooltipContent>
      </Tooltip>
    );
  }

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>{triggerButton}</PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="left" className="max-w-xs">
          {CREATE_REFERENCE_TOOLTIP}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        align="start"
        side="left"
        className="w-auto p-3"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <ReferenceTypePicker
          value={referenceType}
          onChange={setReferenceType}
          onConfirm={() => createReference(referenceType === "static")}
          isPending={isPending}
          disabled={isDisabled}
          idPrefix="references-tab-ref-type"
        />
      </PopoverContent>
    </Popover>
  );
};

export default CreateDocumentReferenceButton;
